import { useEffect, useState } from 'react';
import {
  Button,
  Frame,
  Icon,
  Input,
  Textarea,
  SageClassnames,
  Select,
  useSageBreakpoint,
  SageTokens,
  Divider,
} from '@kajabi/sage-react';
import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk';
import isURL from 'validator/lib/isURL';
import {
  INDUSTRY_SELECT_OPTIONS,
  OPTIMIZELY_CONSTANTS,
  RUDDERSTACK_CONSTANTS,
  rudderstackTrackEvent,
  BUSINESS_TOPIC_MIN_CHARS,
  BUSINESS_TOPIC_MAX_CHARS,
  ERROR_BUSINESS_TOPIC,
  ERROR_YOUTUBE_URL,
  CHANNEL_REGEX,
  HANDLE_REGEX,
  PLAYLIST_REGEX,
  CUSTOM_URL_REGEX,
  USERNAME_REGEX,
} from '../../../utils/constants';

import { StepLayout } from './StepLayout';

const DEFAULT_INDUSTRY = 'placeholder';

interface PropsType extends WithOptimizelyProps {
  onStepChange: () => void;
  contentTab: string;
  handleOnChange: (arg: object) => void;
  optimizely: any;
  userData: { industry: string; business_topic: string; youtube_url: string };
}

const UnwrappedBusinessTopicSlide = ({
  handleOnChange,
  onStepChange,
  optimizely,
  userData,
}: PropsType) => {
  type Errors = {
    [key: string]: string[];
  };

  const [errors, setErrors] = useState<Errors>({
    businessTopic: [],
    youtubeUrl: []
  });

  const {
    industry = DEFAULT_INDUSTRY,
    business_topic: businessTopic = '',
    youtube_url: youtubeUrl = '',
  } = userData;

  const [formValid, setFormValid] = useState(false);

  const breakpoints = useSageBreakpoint();

  const queryParams = new URLSearchParams(window.location.search);
  const email = queryParams.get('email');
  const signupMethod = queryParams.get('signup_method');
  const firstName = queryParams.get('first_name');
  const lastName = queryParams.get('last_name');

  const getFullNameFromParams = (firstName: string, lastName: string) => {
    return `${firstName} ${lastName}`;
  };

  useEffect(() => {
    if (email) {
      handleOnChange({
        target: { id: 'email', value: email.split(' ').join('+') },
      });
    }

    if (signupMethod) {
      handleOnChange({
        target: { id: 'signup_method', value: signupMethod },
      });
    }

    if (firstName && lastName) {
      handleOnChange({
        target: {
          id: 'full_name',
          value: getFullNameFromParams(firstName, lastName),
        },
      });
    }
    rudderstackTrackEvent(
      RUDDERSTACK_CONSTANTS.businessTopicSlideName,
      RUDDERSTACK_CONSTANTS.slideViewed,
      optimizely?.user?.id
    );
  }, []);

  useEffect(() => {
    const businessTopicError =
      businessTopic &&
      (businessTopic.trim().length < Number(BUSINESS_TOPIC_MIN_CHARS) ||
        businessTopic.trim().length > Number(BUSINESS_TOPIC_MAX_CHARS));

    const youtubeUrlError = youtubeUrl && !isSupportedYoutubeUrl();

    setErrors(previousErrors => ({
      ...previousErrors,
      businessTopic: businessTopicError ? [ERROR_BUSINESS_TOPIC] : [],
      youtubeUrl: youtubeUrlError ? [ERROR_YOUTUBE_URL] : [],
    }));

    if (!businessTopicError && !youtubeUrlError) {
      setFormValid(true);
    }
  }, [businessTopic, youtubeUrl]);

  const isSupportedYoutubeUrl = () => {
    const supportedUrls = [CHANNEL_REGEX, HANDLE_REGEX, PLAYLIST_REGEX, CUSTOM_URL_REGEX, USERNAME_REGEX];

    for (let i = 0; i < supportedUrls.length; i++) {
      const match = youtubeUrl.match(supportedUrls[i]);
      if (match) {
        handleOnChange({
          target: { id: 'youtube_url', value: match[0] },
        });

        return true;
      }
    }
  }

  const handleOnComplete = () => {
    if (errors.businessTopic.length > 0 || errors.youtubeUrl.length > 0) {
      setFormValid(false);
      return;
    }

    optimizely.track(OPTIMIZELY_CONSTANTS.aboutBusinessCompleted);
    rudderstackTrackEvent(
      RUDDERSTACK_CONSTANTS.businessTopicSlideName,
      RUDDERSTACK_CONSTANTS.buttonClicked,
      optimizely?.user?.id
    );
    onStepChange();
  };
  
  return (
    <StepLayout
      backgroundColor="#FFFFFF"
      imageUrl="https://kajabi-app-assets.kajabi-cdn.com/ai-lead-gen/dominique.jpg"
    >
      <Frame gap={Frame.GAPS[breakpoints.md ? 'MD' : 'LG']}>
        <Frame gap={Frame.GAPS.XS}>
          <h2 className={SageClassnames.TYPE.HEADING_2}>
            Tell us about your business
          </h2>
          <h6 className={SageClassnames.TYPE.HEADING_6}>
            We’ll use this information to generate a preview of your business.
          </h6>
        </Frame>
        <div style={{ width: '100%' }}>
          <Select
            id="industry"
            label="Which industry is most relevant to your business or idea?"
            onChange={handleOnChange}
            options={INDUSTRY_SELECT_OPTIONS}
            required
            value={industry}
          />
        </div>
        <div style={{ width: '100%' }}>
          <p
            className={`${SageClassnames.TYPE.HEADING_6} ${SageClassnames.SPACERS.SM_BOTTOM}`}
          >
            Help us further personalize your business
          </p>
          <Frame
            background={SageTokens.COLOR_SLIDERS.GREY_200}
            padding={Frame.PADDINGS.MD}
            style={{
              borderRadius: '10px',
            }}
          >
            <Frame gap={Frame.GAPS.XXS}>
              <Frame
                align={Frame.ALIGNMENTS.START_SPREAD}
                direction={Frame.DIRECTIONS.HORIZONTAL}
              >
                <label
                  className="sage-input__label"
                  htmlFor="business_topic"
                  style={{ whiteSpace: 'unset' }}
                >
                  Describe your business in a few words
                </label>
              </Frame>
              <Textarea
                id="business_topic"
                className="sage-frame--width-fill"
                onChange={handleOnChange}
                placeholder="Examples: Advanced yoga training for women, social media marketing for business owners..."
                value={businessTopic}
                style={{ height: 120 }}
                hasError={!formValid && errors.businessTopic?.length > 0}
                message={!formValid && errors.businessTopic?.join(', ')}
                required
              />
            </Frame>
            <Frame
              align={Frame.ALIGNMENTS.CENTER_CENTER}
              gap={Frame.GAPS.SM}
              direction={Frame.DIRECTIONS.HORIZONTAL}
            >
              <Divider color={SageTokens.COLOR_PALETTE.GREY_400} />
              <p
                className={`${SageClassnames.TYPE.BODY_SMALL} ${SageClassnames.TYPE_COLORS.CHARCOAL_200}`}
              >
                Or
              </p>
              <Divider color={SageTokens.COLOR_PALETTE.GREY_400} />
            </Frame>

            <Frame gap={Frame.GAPS.XXS}>
              <Frame
                align={Frame.ALIGNMENTS.START_SPREAD}
                direction={Frame.DIRECTIONS.HORIZONTAL}
              >
                <label
                  className="sage-input__label"
                  htmlFor="youtube_url"
                  style={{ whiteSpace: 'unset' }}
                >
                  Add your Youtube channel or playlist url (Optional)
                </label>
              </Frame>
              <Frame direction={Frame.DIRECTIONS.HORIZONTAL}>
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Icon
                    icon={Icon.ICONS.LOGO_YOUTUBE}
                    size="4xl"
                    color="red-300"
                  />
                </div>
                <Input
                  id="youtube_url"
                  className="sage-frame--width-fill"
                  placeholder="https://www.youtube.com/yourchannel"
                  hasError={!formValid && errors.youtubeUrl?.length > 0}
                  message={!formValid && errors.youtubeUrl?.join(', ')}
                  onChange={handleOnChange}
                  value={youtubeUrl}
                />
              </Frame>
            </Frame>
          </Frame>
        </div>

        <Frame
          align={Frame.ALIGNMENTS.BASELINE_RIGHT}
          direction={Frame.DIRECTIONS.HORIZONTAL}
        >
          <Button
            disabled={
              industry === DEFAULT_INDUSTRY || (businessTopic.trim() === '' && youtubeUrl.trim() === '')
            }
            fullWidth={breakpoints.md}
            onClick={handleOnComplete}
          >
            Continue
          </Button>
        </Frame>
      </Frame>
    </StepLayout>
  );
};

export const BusinessTopicSlide = withOptimizely(UnwrappedBusinessTopicSlide);
